import { Box, Button, Chip, Tooltip, Typography, useTheme } from '@mui/material';
import { useIntl } from 'react-intl';

import type { AccuracyRunView } from '../../hook/useAccuracyRunHistory';
import { StatusDot } from '../StatusIcon';
import { useAccuracyRunRow } from './useAccuracyRunRow';

interface AccuracyRunRowProps {
  view: AccuracyRunView;
  onViewResults: (view: AccuracyRunView) => void;
  onCancel: (view: AccuracyRunView) => void;
  isCancelPending: boolean;
}

/**
 * Presentational row for a single accuracy run — status chip, job reference, run
 * configuration, relative timestamp and the contextual action. All display
 * derivation lives in `useAccuracyRunRow`; the modal owns queries and navigation.
 */
// TODO: cleanup with cp_trigger_accuracy_from_ui ff
export const AccuracyRunRow = ({
  view,
  onViewResults,
  onCancel,
  isCancelPending,
}: AccuracyRunRowProps) => {
  const theme = useTheme();
  const { formatMessage } = useIntl();
  const { statusChip, jobReference, secondaryLine, etaLabel, timestampLabel, actionType } =
    useAccuracyRunRow(view);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: theme.spacing(4),
        py: theme.spacing(4),
        borderBottom: `1px solid ${theme.palette.divider}`,
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(1), minWidth: 0 }}>
        <Box
          sx={{ display: 'flex', alignItems: 'center', gap: theme.spacing(2), flexWrap: 'wrap' }}
        >
          <Chip
            icon={statusChip.dotColor ? <StatusDot color={statusChip.dotColor} /> : undefined}
            label={statusChip.label}
            size="small"
            sx={{
              backgroundColor: statusChip.bg,
              color: statusChip.fg,
              fontWeight: 400,
              fontSize: '11px',
              px: theme.spacing(3),
              '& .MuiChip-icon': { ml: theme.spacing(2) },
            }}
          />
          <Typography variant="caption" color="text.secondary">
            {jobReference}
          </Typography>
        </Box>

        {secondaryLine && (
          <Typography variant="body2" color="text.primary" sx={{ wordBreak: 'break-word' }}>
            {secondaryLine}
          </Typography>
        )}

        {etaLabel && (
          <Typography variant="caption" color="text.secondary">
            {etaLabel}
          </Typography>
        )}

        {timestampLabel && (
          <Typography variant="caption" color="text.secondary">
            {timestampLabel}
          </Typography>
        )}
      </Box>

      {actionType === 'cancel' && (
        <Button
          variant="outlined"
          color="error"
          size="small"
          disabled={isCancelPending}
          onClick={() => onCancel(view)}
          sx={{ flexShrink: 0, textTransform: 'none' }}
        >
          {formatMessage({ id: 'adminProjects.accuracyRunsModal.run.cancel' })}
        </Button>
      )}

      {actionType === 'cancel-disabled' && (
        <Tooltip
          title={formatMessage({
            id: 'adminProjects.accuracyRunsModal.run.cancelUnavailableExporting',
          })}
        >
          <Box component="span" sx={{ flexShrink: 0 }}>
            <Button
              variant="outlined"
              color="error"
              size="small"
              disabled
              sx={{ textTransform: 'none' }}
            >
              {formatMessage({ id: 'adminProjects.accuracyRunsModal.run.cancel' })}
            </Button>
          </Box>
        </Tooltip>
      )}

      {actionType === 'view-results' && (
        <Button
          variant="outlined"
          size="small"
          onClick={() => onViewResults(view)}
          sx={{ flexShrink: 0, textTransform: 'none' }}
        >
          {formatMessage({ id: 'adminProjects.accuracyRunsModal.run.viewResults' })}
        </Button>
      )}

      {actionType === 'view-results-disabled' && (
        <Tooltip
          title={formatMessage({ id: 'adminProjects.accuracyRunsModal.run.resultsUnavailable' })}
        >
          <Box component="span" sx={{ flexShrink: 0 }}>
            <Button variant="outlined" size="small" disabled sx={{ textTransform: 'none' }}>
              {formatMessage({ id: 'adminProjects.accuracyRunsModal.run.viewResults' })}
            </Button>
          </Box>
        </Tooltip>
      )}
    </Box>
  );
};
